import { checkCanConfirm, LEAD_LEVEL, RANKS } from '@dc1/shared';
import { Link } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { Dashboard, Me, Progress } from '../../api/queries';
import type { ContentView } from '../../lib/content';
import { levelCode } from '../../lib/format';
import { LeadStar } from '../../ui/LeadStar';
import { Sheet } from '../../ui/Sheet';
import { initials } from '../members/people';
import { CoverageLegend, CoverageRose, PeriodProgress } from './Coverage';
import { areaLeads, coverageByGroup, pendingAreas, personLeads, riskCoverers } from './dashboard-model';
import f from './focus.module.css';
import { Heatmap } from './Heatmap';
import { Ranking } from './Ranking';

/** The details a dashboard KPI or card opens (design v3 "focus" layout), kept in the URL. */
export const DASH_PANELS = ['members', 'rank', 'pending', 'cover', 'leads', 'heat', 'progress'] as const;
export type DashPanel = (typeof DASH_PANELS)[number];

/** The title of a detail: the sheet's heading, and the back button of a member's drawer opened from it. */
export function panelTitle(t: TFunction, panel: DashPanel, content: ContentView): string {
  const areaCount = content.groups.reduce((n, g) => n + g.areas.length, 0);
  const titles: Record<DashPanel, string> = {
    members: t('dashboard.sheetMembers'),
    rank: t('dashboard.sheetRank'),
    pending: t('dashboard.sheetPending'),
    cover: t('dashboard.coverTitle', { n: areaCount }),
    leads: t('dashboard.sheetLeads'),
    heat: t('dashboard.heatTitle'),
    progress: t('dashboard.deltaTitle'),
  };
  return titles[panel];
}

export function DashSheet({
  panel,
  data,
  progress,
  content,
  me,
  covered,
  onOpen,
  onClose,
}: {
  panel: DashPanel;
  data: Dashboard;
  progress: Progress;
  content: ContentView;
  me: Me;
  /** A member's drawer is open over it. */
  covered: boolean;
  /** Opens a member's drawer over the sheet. */
  onOpen: (memberId: string) => void;
  /** Called once the sheet has slid out. */
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const meId = me.user.id;
  return (
    <Sheet title={panelTitle(t, panel, content)} covered={covered} onClose={onClose}>
      {panel === 'members' ? (
        <MembersByLevel data={data} content={content} meId={meId} onOpen={onOpen} />
      ) : null}
      {panel === 'rank' ? (
        <Ranking members={data.members} meId={meId} content={content} onOpen={onOpen} bare />
      ) : null}
      {panel === 'pending' ? <PendingList data={data} me={me} onOpen={onOpen} /> : null}
      {panel === 'cover' ? <CoverDetail data={data} content={content} /> : null}
      {panel === 'leads' ? <LeadsDetail data={data} content={content} onOpen={onOpen} /> : null}
      {panel === 'heat' ? (
        <Heatmap members={data.members} meId={meId} content={content} onOpen={onOpen} bare />
      ) : null}
      {panel === 'progress' ? (
        <PeriodProgress
          members={data.members}
          progress={progress}
          periodId={data.periodId}
          onOpen={onOpen}
          bare
        />
      ) : null}
    </Sheet>
  );
}

function PersonRow({
  name,
  sub,
  score,
  onOpen,
  children,
}: {
  name: string;
  sub?: string;
  score: number;
  onOpen: () => void;
  children?: React.ReactNode;
}) {
  return (
    <button type="button" className={f.personRow} onClick={onOpen}>
      <span className={f.avatar} aria-hidden="true">
        {initials(name)}
      </span>
      <span className={f.personMain}>
        <span className={f.personName}>{name}</span>
        {children}
      </span>
      {sub ? <span className={f.personSub}>{sub}</span> : null}
      <b className={f.personScore}>{score}</b>
    </button>
  );
}

/** Everyone by Level, highest Level first, each with their confirmed score. */
function MembersByLevel({
  data,
  content,
  meId,
  onOpen,
}: {
  data: Dashboard;
  content: ContentView;
  meId: string;
  onOpen: (memberId: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div>
      {[...RANKS].reverse().map((rank) => {
        const rows = data.members.filter((m) => m.rank === rank);
        if (!rows.length) return null;
        return (
          <section key={rank} className={f.sheetGroup} aria-label={content.ranks[rank].name}>
            <h3 className={f.sheetGroupHead}>
              <span className={f.swatch} style={{ background: `var(--zone-${rank})` }} aria-hidden="true" />
              {content.ranks[rank].name}
              <span>· {t('dashboard.zoneCount', { n: rows.length })}</span>
            </h3>
            {rows.map((m) => (
              <PersonRow
                key={m.id}
                name={m.id === meId ? t('dashboard.meShort', { name: m.displayName }) : m.displayName}
                sub={t(`roles.${m.role}`)}
                score={m.effectiveScore}
                onOpen={() => {
                  onOpen(m.id);
                }}
              />
            ))}
          </section>
        );
      })}
    </div>
  );
}

/** People with levels waiting that the viewer may confirm, most waiting first. */
function PendingList({ data, me, onOpen }: { data: Dashboard; me: Me; onOpen: (memberId: string) => void }) {
  const { t } = useTranslation();
  const actor = { id: me.user.id, role: me.user.role };
  const rows = data.members
    .filter((m) => m.pendingCount > 0 && checkCanConfirm(actor, { id: m.id, role: m.role }).ok)
    .sort((a, b) => b.pendingCount - a.pendingCount);
  if (!rows.length) return <p className={f.empty}>{t('dashboard.pendingNone')}</p>;
  return (
    <div>
      <p className={f.sheetNote}>{t('dashboard.pendingNote')}</p>
      {rows.map((m) => (
        <PersonRow
          key={m.id}
          name={m.displayName}
          score={m.pendingCount}
          onOpen={() => {
            onOpen(m.id);
          }}
        >
          <span className={f.chips}>
            {pendingAreas(m).map((a) => (
              // An L5 waiting (ADR 0018) stands out in gold: confirming it names someone to lead the area.
              <span key={a.areaId} className={f.chip} data-claim={a.level === LEAD_LEVEL}>
                {a.level === LEAD_LEVEL ? <LeadStar size={11} /> : null}
                {a.areaId} · {levelCode(a.level)}
              </span>
            ))}
          </span>
        </PersonRow>
      ))}
    </div>
  );
}

/**
 * Who leads each area at a confirmed L5, who claims L5 waiting for a confirmation (apart), each person's L5
 * areas, and the areas nobody leads yet (ADR 0018). Leaders and Sub-Leaders only: it names people with levels.
 */
function LeadsDetail({
  data,
  content,
  onOpen,
}: {
  data: Dashboard;
  content: ContentView;
  onOpen: (memberId: string) => void;
}) {
  const { t } = useTranslation();
  const areas = areaLeads(
    data.members,
    data.coverage.map((c) => c.areaId),
  );
  const shown = areas.filter((a) => a.leads.length + a.claims.length > 0);
  const led = areas.filter((a) => a.leads.length > 0).length;
  const nobody = areas.filter((a) => a.leads.length === 0);
  const people = personLeads(data.members);
  const l5 = levelCode(LEAD_LEVEL);
  return (
    <div className={f.sheetStack}>
      <p className={f.sheetNote}>{t('dashboard.leadsNote')}</p>
      <section aria-labelledby="sheet-leads-areas">
        <h3 id="sheet-leads-areas" className={f.subTitle}>
          {t('dashboard.leadsByArea', { n: led, total: areas.length })}
        </h3>
        {shown.length === 0 ? <p className={f.empty}>{t('dashboard.leadsNone')}</p> : null}
        {shown.length ? (
          <ul className={f.riskList}>
            {shown.map((a) => (
              <li key={a.areaId} className={f.riskRow}>
                <span className={f.riskName}>
                  {a.areaId} {content.areaById.get(a.areaId)?.name}
                </span>
                <b className={f.leadN}>{t('dashboard.leadsPeople', { n: a.leads.length })}</b>
                <span className={f.riskWho}>
                  {[
                    ...a.leads.map((m) => m.displayName),
                    ...a.claims.map((m) => t('dashboard.leadsClaim', { name: m.displayName })),
                  ].join(', ')}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </section>
      {people.length ? (
        <section aria-labelledby="sheet-leads-people">
          <h3 id="sheet-leads-people" className={f.subTitle}>
            {t('dashboard.leadsByPerson', { n: people.length })}
          </h3>
          {people.map((p) => (
            <PersonRow
              key={p.member.id}
              name={p.member.displayName}
              score={p.leads.length}
              onOpen={() => {
                onOpen(p.member.id);
              }}
            >
              <span className={f.chips}>
                {p.leads.map((id) => (
                  <span key={id} className={f.chip} data-lead="true">
                    {id} · {l5}
                  </span>
                ))}
                {p.claims.map((id) => (
                  <span key={id} className={f.chip} data-claim="true">
                    <LeadStar size={11} />
                    {t('dashboard.leadsClaimChip', { area: id, code: l5 })}
                  </span>
                ))}
              </span>
            </PersonRow>
          ))}
        </section>
      ) : null}
      <section aria-labelledby="sheet-leads-nobody">
        <h3 id="sheet-leads-nobody" className={f.subTitle}>
          {t('dashboard.leadsNobody', { n: nobody.length })}
        </h3>
        <p className={f.leadsNobody}>{nobody.map((a) => a.areaId).join(' · ')}</p>
      </section>
    </div>
  );
}

/** The rose, who covers each risk area, and how many areas each group has covered. */
function CoverDetail({ data, content }: { data: Dashboard; content: ContentView }) {
  const { t } = useTranslation();
  const risks = riskCoverers(data.members, data.coverage);
  const groups = coverageByGroup(data.coverage, content);
  return (
    <div className={f.sheetStack}>
      <div className={f.roseBox}>
        <CoverageRose coverage={data.coverage} content={content} teamSize={data.members.length} />
      </div>
      <CoverageLegend riskCount={risks.length} />
      <section aria-labelledby="sheet-risk">
        <h3 id="sheet-risk" className={f.subTitle}>
          {t('dashboard.riskTitle', { n: risks.length })}
        </h3>
        {risks.length === 0 ? <p className={f.empty}>{t('dashboard.noRisk')}</p> : null}
        {risks.length ? (
          <ul className={f.riskList}>
            {risks.map((r) => (
              <li key={r.areaId} className={f.riskRow}>
                <span className={f.riskName}>
                  {r.areaId} {content.areaById.get(r.areaId)?.name}
                </span>
                <b className={f.riskN}>{t('dashboard.riskPeople', { n: r.count })}</b>
                <span className={f.riskWho}>
                  {r.names.length
                    ? t('dashboard.riskOnly', { names: r.names.join(', ') })
                    : t('dashboard.riskNobody')}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </section>
      <section className={f.sheetStack} aria-labelledby="sheet-groups">
        <h3 id="sheet-groups" className={f.subTitle}>
          {t('dashboard.coverByGroup')}
        </h3>
        {groups.map((g) => (
          <div key={g.groupId} className={f.covGroup}>
            <span>
              {g.n}. {g.short}
            </span>
            <span className={f.covCells} aria-hidden="true">
              {g.bars.map((b) => (
                <span
                  key={b.areaId}
                  data-risk={b.risk}
                  title={t('dashboard.coverageBar', { area: b.areaId, name: b.name, n: b.count })}
                />
              ))}
            </span>
            <span className={f.covOk}>
              {g.bars.filter((b) => !b.risk).length}/{g.bars.length}
            </span>
          </div>
        ))}
      </section>
      <Link to="/team" className={f.sheetLink}>
        {t('dashboard.coverToTeam')}
      </Link>
    </div>
  );
}
